import { Component } from '@theme/component';

const attributes = {
  HIDDEN: 'hidden',
  EXPANDED: 'expanded',
  LINE_CLAMP: '--line-clamp',
};

/** @extends {Component<{content: HTMLElement, button: HTMLElement}>} */
class ReadMore extends Component {
  requiredRefs = ['content', 'button'];

  /** @type {Animation | null} */
  #activeAnimation = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    requestAnimationFrame(() => this.#updateClampingState());

    window.addEventListener('resize', () => this.#updateClampingState(), {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #updateClampingState() {
    const { content, button } = this.refs;

    if (!content || !button || this.hasAttribute(attributes.EXPANDED)) {
      return;
    }

    button.classList.toggle(attributes.HIDDEN, content.scrollHeight <= content.clientHeight);
  }

  /**
   * @param {number} start
   * @param {number} end
   * @param {Function} [callback]
   */
  #animate(start, end, callback) {
    const { content } = this.refs;

    this.#activeAnimation?.cancel();
    content.style.maxHeight = `${start}px`;

    this.#activeAnimation = content.animate(
      { maxHeight: [`${start}px`, `${end}px`] },
      { duration: 300, easing: 'ease-in-out' },
    );

    this.#activeAnimation.onfinish = () => {
      content.style.removeProperty('max-height');
      if (callback) callback();
      this.#activeAnimation = null;
    };
  }

  /** @param {Event} [event] */
  toggle(event) {
    if (event) event.preventDefault();

    const { content, button } = this.refs;
    const startHeight = content.offsetHeight;
    const isExpanded = this.toggleAttribute(attributes.EXPANDED);

    if (isExpanded) {
      content.style.setProperty(attributes.LINE_CLAMP, '0');
      button.textContent = this.dataset.labelLess || '';
      this.#animate(startHeight, content.offsetHeight);
    } else {
      // 1. Temporarily remove clamp to measure actual target height
      content.style.removeProperty(attributes.LINE_CLAMP);
      const endHeight = content.offsetHeight;

      // 2. Restore line-clamp: 0 so content remains full during animation
      content.style.setProperty(attributes.LINE_CLAMP, '0');
      button.textContent = this.dataset.labelMore || '';

      // 3. Remove line-clamp property only after animation finishes
      this.#animate(startHeight, endHeight, () => {
        content.style.removeProperty(attributes.LINE_CLAMP);
      });
    }
  }
}

if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore);
}
